import { updateValuesWrapper } from "@repo/dynamicEditor/src/helpers/updatePropertiesHooks";
import {
	applyFieldAuditBusinessLogic,
	IActiveLists,
	mapListToResponse,
	mapResponseToList,
	useSharedRepFieldAuditsLists,
} from "@repo/field-audits";
import { PushProcessingNotification } from "@repo/helpers";
import { Loading, Tabs, TabsContent, TabsList, TabsTrigger } from "@repo/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useLocationApi } from "../../../api/locationApi";
import * as Queries from "../../../api/queries";
import { useRepFieldAuditApi, useRepFieldAuditQueries } from "../../../api/repFieldAuditApi";
import { useSettingsApi } from "../../../api/settingsApi";
import { EditableItemDynamicTab } from "../../../components/dynamicEditor/dynamicTab/editableItem";
import { ITemplateData } from "../../../components/dynamicEditor/templateDataHook";
import { useLocationContext, useRepFieldAuditContext } from "../../../contexts/editableItemContext";
import { useUserContext } from "../../../contexts/userContext";
import { tabs } from "../../../string";
import FieldAuditHistoryGridBasic from "../history";

export interface IState extends ITemplateData {
	activeLists: IActiveLists;
}

function useRepFieldAuditsLists() {
	const { useSettingsQuery } = useSettingsApi();
	const { useFormTypesQuery, useQuestionOptionsQuery, useLocationsQuery } = useRepFieldAuditQueries();
	const { getReps } = useLocationApi();

	return useSharedRepFieldAuditsLists({
		useFormTypesQuery,
		useQuestionOptionsQuery,
		useLocationsQuery,
		getReps,
		useSettingsQuery,
	});
}

function OnboardingAuditEditorDynamic() {
	const { locationId, locationLoaded } = useLocationContext();
	const { user } = useUserContext();
	const context = useRepFieldAuditContext();
	const { item: repFieldAudit, overwriteItem } = context;
	const auditId = repFieldAudit?.representativeFieldAuditId;
	const [activeLists, activeListsLoaded] = useRepFieldAuditsLists();
	const { getFieldAuditQuestions, saveFieldAudit } = useRepFieldAuditApi();
	const { useQuestionsQuery, useRepFieldAuditQuery } = useRepFieldAuditQueries();
	const { clearQuery: clearFieldAuditQuery } = useRepFieldAuditQuery();
	const {
		data: fieldAuditQuestions,
		loaded: fieldAuditQuestionsLoaded,
		clearQuery: clearFieldAuditQuestionsQuery,
	} = useQuestionsQuery(auditId, repFieldAudit?.formTypeId);
	const [activeTab, setActiveTab] = useState("editor");
	const { useFieldAuditHistories } = useRepFieldAuditQueries();
	const { clearQuery: clearFieldAuditHistoryQuery } = useFieldAuditHistories(auditId);
	const queryClient = useQueryClient();

	const allLoaded = locationLoaded && activeListsLoaded && fieldAuditQuestionsLoaded;

	useEffect(() => {
		// If we are on the history tab and there is no audit ID, switch to the editor tab
		if (activeTab === "editor") return;
		if (auditId) return;

		setActiveTab("editor");
	}, [activeTab, setActiveTab, auditId]);

	const templateData = useMemo(
		() => ({
			repFieldAudit,
			user,
			activeLists,
		}),
		[repFieldAudit, user, activeLists, allLoaded],
	);

	useEffect(() => {
		if (fieldAuditQuestions && allLoaded) {
			overwriteItem({
				...fieldAuditQuestions,
				...repFieldAudit,
				responseMap: {},
			});
		}
	}, [fieldAuditQuestions, auditId, allLoaded]);

	useEffect(() => {
		if (repFieldAudit?.questionGroups?.length > 0 && allLoaded) {
			const { responseMap, score, scoreTotal } = mapListToResponse(repFieldAudit, activeLists);
			overwriteItem({
				...repFieldAudit,
				score: scoreTotal > 0 ? score / scoreTotal : 0,
				scoreAmount: score,
				scoreTotal,
				responseMap,
			});
		}
	}, [repFieldAudit?.questionGroups, allLoaded, auditId]);

	const save = useCallback(
		async (email: boolean) => {
			const notify = PushProcessingNotification("Saving Field Audit");
			try {
				mapResponseToList(repFieldAudit);
				const id = await saveFieldAudit(repFieldAudit, email);
				notify.processing("Loading...");
				const newFieldAudit = await getFieldAuditQuestions(locationId, id, undefined);
				await clearFieldAuditQuery();
				await clearFieldAuditQuestionsQuery();
				await clearFieldAuditHistoryQuery();
				await queryClient.invalidateQueries({ queryKey: [Queries.GROUP.LOCATION] });

				const { responseMap, score, scoreTotal } = mapListToResponse(newFieldAudit, activeLists);
				notify.success("Saved!");
				return {
					...newFieldAudit,
					score: scoreTotal > 0 ? score / scoreTotal : 0,
					scoreAmount: score,
					scoreTotal,
					responseMap,
				};
			} catch (ex) {
				notify.error("Failed to save");
				throw ex;
			}
		},
		[repFieldAudit],
	);

	const applyBusinessLogic = async (id: string, value: any, newState: any) => {
		return applyFieldAuditBusinessLogic(id, value, newState, repFieldAudit);
	};

	const updateValues = useMemo(() => updateValuesWrapper(applyBusinessLogic), [applyBusinessLogic]);

	return (
		<Loading loading={!allLoaded}>
			<Tabs value={activeTab} onValueChange={setActiveTab}>
				{auditId ? (
					<TabsList>
						<TabsTrigger value="editor">Editor</TabsTrigger>
						<TabsTrigger value="history">History</TabsTrigger>
					</TabsList>
				) : null}
				<TabsContent value="editor">
					<EditableItemDynamicTab
						itemContext={context}
						pageName={tabs.fieldAudits}
						otherTemplateData={templateData}
						itemProp="repFieldAudit"
						updateValues={updateValues}
						save={save}
						loaded
						readonly={!user.userIsAdmin}
					/>
				</TabsContent>
				<TabsContent value="history">
					<FieldAuditHistoryGridBasic fieldAuditId={auditId} />
				</TabsContent>
			</Tabs>
		</Loading>
	);
}

export default OnboardingAuditEditorDynamic;
